import React, { createContext, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  auth,
  createUserWithEmailAndPassword,
  facebookAuthProvider,
  githubAuthProvider,
  googleAuthProvider,
  onAuthStateChanged,
  sendEmailVerification,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  twitterAuthProvider,
  updateProfile,
} from './firebase';
import { useDispatch } from 'react-redux';
import {
  FETCH_ERROR,
  FETCH_START,
  FETCH_SUCCESS,
} from 'shared/constants/ActionTypes';
import { getProfileData } from '@crema/services/apis/profile';
import { fetchSuccess } from 'redux/actions';
import { API, AuthSSO } from 'shared/constants/AppEnums';

const FirebaseContext = createContext();
const FirebaseActionsContext = createContext();

export const useFirebase = () => useContext(FirebaseContext);

export const useFirebaseActions = () => useContext(FirebaseActionsContext);

const listRoleMock = [
  { id: 'SIPEG_PEGAWAI', label: 'Pegawai' },
  { id: 'SIPEG_OPERATOR', label: 'Operator' },
  { id: 'SIPEG_ADMINISTRATOR', label: 'Administrator' },
  { id: 'SIPEG_VIEWER', label: 'Viewer' },
  { id: 'SIPEG_OPERATOR_ESELON_1', label: 'Operator Eselon 1' },
  { id: 'SIPEG_ADMIN_360', label: 'Admin Penilaian 360' },
  { id: 'SIPEG_OPERATOR_360', label: 'Operator Penilaian 360' },
  { id: 'SIPEG_OPERATOR_ESELON_1_360', label: 'Operator Eselon 1 Penilaian 360' }
]

const FirebaseAuthProvider = ({ children }) => {
  const [isLogout, setIsLogout] = useState(false)
  const [firebaseData, setFirebaseData] = useState({
    user: undefined,
    isLoading: true,
    listRole: [],
    menuList: [],
    currentRole: '',
    isAuthenticated: false,
    isAdminCoofis: false,
  });
  const dispatch = useDispatch();

  useEffect(() => {
    const getAuthUser = onAuthStateChanged(
      auth,
      (user) => {
        setFirebaseData({
          user: user,
          isAuthenticated: Boolean(user),
          isLoading: false,
        });
      },
      () => {
        setFirebaseData({
          user: undefined,
          isLoading: false,
          isAuthenticated: false,
        });
      },
      (completed) => {
        setFirebaseData({
          user: undefined,
          isLoading: false,
          isAuthenticated: completed,
        });
      },
    );

    return () => {
      getAuthUser();
    };
  }, []);

  const loginUser = (token) => {
    // hit api profile pegawai
    getProfileData(token)
      .then(res => {
        if (res.success) {
          const data = res?.results[0]
          // check di localstorage ada value ga
          let userRole = localStorage.getItem('userRole')
          const value = userRole ? JSON.parse(userRole) : ''
          const selectedRole = listRoleMock.find(item => item.id === data.role)
          if (!value) {
            localStorage.setItem('userRole', JSON.stringify({
              nip: data.nip,
              role: selectedRole
            }))
          } else {
            // check nip 
            if (value.nip !== data.nip) {
              localStorage.setItem('userRole', JSON.stringify({
                nip: data.nip,
                role: selectedRole
              }))
            }
          }

          setFirebaseData({
            user: data,
            isLoading: false,
            isAuthenticated: true,
            listRole: listRoleMock,
            menuList: data?.menu_access,
            currentRole: !value ? selectedRole : value?.role,
            isAdminCoofis: data?.nip === '101010101'
          });
          // set loading to false
          dispatch(fetchSuccess())
        }
      })
      .catch(err => {
        console.error(err)
        setFirebaseData({
          ...firebaseData,
          isAuthenticated: false,
          isLoading: false,
        });
        // dispatch({ type: FETCH_ERROR, payload: err.message })
      })
  }

  const getProvider = (providerName) => {
    switch (providerName) {
      case 'google': {
        return googleAuthProvider;
      }
      case 'facebook': {
        return facebookAuthProvider;
      }
      case 'twitter': {
        return twitterAuthProvider;
      }
      case 'github': {
        return githubAuthProvider;
      }
      default:
        return googleAuthProvider;
    }
  };

  const logInWithPopup = async (providerName) => {
    dispatch({ type: FETCH_START });
    try {
      const { user } = await signInWithPopup(auth, getProvider(providerName));
      setFirebaseData({
        user,
        isAuthenticated: true,
        isLoading: false,
      });
      dispatch({ type: FETCH_SUCCESS });
    } catch (error) {
      setFirebaseData({
        ...firebaseData,
        isAuthenticated: false,
        isLoading: false,
      });
      dispatch({ type: FETCH_ERROR, payload: error.message });
    }
  };

  const logInWithEmailAndPassword = async ({ email, password }) => {
    dispatch({ type: FETCH_START });
    try {
      const { user } = await signInWithEmailAndPassword(auth, email, password);
      setFirebaseData({ user, isAuthenticated: true, isLoading: false });
      dispatch({ type: FETCH_SUCCESS });
    } catch (error) {
      setFirebaseData({
        ...firebaseData,
        isAuthenticated: false,
        isLoading: false,
      });
      dispatch({ type: FETCH_ERROR, payload: error.message });
    }
  };

  const registerUserWithEmailAndPassword = async ({ name, email, password }) => {
    dispatch({ type: FETCH_START });
    try {
      const { user } = await createUserWithEmailAndPassword(
        auth,
        email,
        password,
      );
      await sendEmailVerification(auth.currentUser, {
        url: window.location.href,
        handleCodeInApp: true,
      });
      await updateProfile(auth.currentUser, {
        displayName: name,
      });
      setFirebaseData({
        user: { ...user, displayName: name },
        isAuthenticated: true,
        isLoading: false,
      });
      dispatch({ type: FETCH_SUCCESS });
    } catch (error) {
      setFirebaseData({
        ...firebaseData,
        isAuthenticated: false,
        isLoading: false,
      });
      dispatch({ type: FETCH_ERROR, payload: error.message });
    }
  };

  const logout = async () => {
    setFirebaseData({ ...firebaseData, isLoading: true });
    try {
      await signOut(auth);
      setFirebaseData({
        user: null,
        isLoading: false,
        isAuthenticated: false,
      });

      localStorage.removeItem('token')
    } catch (error) {
      setFirebaseData({
        user: null,
        isLoading: false,
        isAuthenticated: false,
      });
    }
  };

  // changeRoleActive
  const setRole = (nip, role) => {
    const selectedRole = listRoleMock.find(item => item.id === role)
    localStorage.setItem('userRole', JSON.stringify({ nip: nip, role: selectedRole }))
    setFirebaseData({
      ...firebaseData,
      currentRole: selectedRole
    })

    if (!selectedRole.label?.includes('360')) {
      window.location.replace('/dashboard')
    } else {
      window.location.replace('/umpan-balik-360')
    }
  }

  // logout epeg
  const logoutEpeg = async () => {
    setFirebaseData({ ...firebaseData, isLoading: true });
    try {
      const token = sessionStorage.getItem('token');
      // alert(API.LOG_OUT_URL + token)
      window.location.href = API.LOG_OUT_URL + token;

      sessionStorage.removeItem('token');
      sessionStorage.removeItem('urlRedirect')
      localStorage.removeItem('userRole');
      setIsLogout(true)

      // setFirebaseData({
      //   user: null,
      //   isLoading: false,
      //   isAuthenticated: false,
      // });
    } catch (error) {
      setFirebaseData({
        user: null,
        isLoading: false,
        isAuthenticated: false,
      });
    }
  };

  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const token = urlParams.get('token');

  if (window.location.pathname !== '/' && window.location.pathname !== '/redirect') {
    if (isLogout == false) {
      console.log('set url', window.location.pathname)
      sessionStorage.setItem('urlRedirect', window.location.pathname);
    }
  }

  if (token !== null) {
    // const url = sessionStorage.getItem('urlRedirect')
    sessionStorage.setItem('token', token);
    // if (url === null || url === undefined) {
    //   window.location.replace('/dashboard')
    // } else {
    //   window.location.replace(url)
    // }
  }

  setTimeout(() => {
    const token_login = sessionStorage.getItem('token');
    if (token_login === null) window.location.replace(AuthSSO);
  }, 500)

  return (
    <FirebaseContext.Provider
      value={{
        ...firebaseData,
      }}
    >
      <FirebaseActionsContext.Provider
        value={{
          logInWithEmailAndPassword,
          registerUserWithEmailAndPassword,
          logInWithPopup,
          loginUser,
          setRole,
          logout,
          logoutEpeg
        }}
      >
        {children}
      </FirebaseActionsContext.Provider>
    </FirebaseContext.Provider>
  );
};
export default FirebaseAuthProvider;

FirebaseAuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
